import { FC } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CardTravelIcon from '@mui/icons-material/CardTravel';
import LinkIcon from '@mui/icons-material/Link';

export type PaymentMethodFilterValue = {
    qr: boolean;
    cash: boolean;
    payment_link: boolean;
    other: boolean;
};

type Props = {
    value: PaymentMethodFilterValue;
    onChange: (value: PaymentMethodFilterValue) => void;
};

export const FilterContainerPaymentMethod: FC<Props> = ({ value, onChange }) => {
    const { t }: { t: any } = useTranslation();

    const handleToggle = (method: 'qr' | 'cash' | 'payment_link' | 'other') => {
        const next: PaymentMethodFilterValue = {
            ...value,
            [method]: !value[method]
        };
        onChange(next);
    };

    const circleBaseSx = {
        width: 40,
        height: 40,
        borderRadius: '50%',
        borderWidth: 1,
        borderStyle: 'solid',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0
    } as const;

    const getQrStyles = (selected: boolean) => ({
        ...circleBaseSx,
        borderColor: selected ? '#1A75FF' : '#C5CAD1',
        backgroundColor: selected ? '#E7F0FF' : 'transparent',
        '& svg': {
            fontSize: 24,
            color: selected ? '#1A75FF' : '#C5CAD1'
        }
    });

    const getCashStyles = (selected: boolean) => ({
        ...circleBaseSx,
        borderColor: selected ? '#1A75FF' : '#C5CAD1',
        backgroundColor: selected ? '#E7F0FF' : 'transparent',
        '& svg': {
            fontSize: 24,
            color: selected ? '#44D600' : '#C5CAD1'
        }
    });

    const getPaymentLinkStyles = (selected: boolean) => ({
        ...circleBaseSx,
        borderColor: selected ? '#1A75FF' : '#C5CAD1',
        backgroundColor: selected ? '#E7F0FF' : 'transparent',
        '& svg': {
            fontSize: 24,
            color: selected ? '#FFA31A' : '#C5CAD1'
        }
    });

    const getOtherStyles = (selected: boolean) => ({
        ...circleBaseSx,
        borderColor: selected ? '#1A75FF' : '#C5CAD1',
        backgroundColor: selected ? '#E7F0FF' : 'transparent',
        '& svg': {
            fontSize: 24,
            color: selected ? '#636161' : '#C5CAD1'
        }
    });

    return (
        <Box
            display="flex"
            alignItems="center"
            sx={{
                minWidth: 100,
                height: 40,
                columnGap: 1.5
            }}
        >
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 700,
                    lineHeight: '100%',
                    color: '#223354'
                }}
            >
                {t('Payment method')}
            </Typography>

            <Box display="flex" columnGap={1}>
                <IconButton
                    onClick={() => handleToggle('qr')}
                    sx={getQrStyles(value.qr)}
                >
                    <QrCode2Icon />
                </IconButton>

                <IconButton
                    onClick={() => handleToggle('cash')}
                    sx={getCashStyles(value.cash)}
                >
                    <MonetizationOnIcon />
                </IconButton>

                <IconButton
                    onClick={() => handleToggle('payment_link')}
                    sx={getPaymentLinkStyles(value.payment_link)}
                >
                    <LinkIcon />
                </IconButton>

                <IconButton
                    onClick={() => handleToggle('other')}
                    sx={getOtherStyles(value.other)}
                >
                    <CardTravelIcon />
                </IconButton>
            </Box>
        </Box>
    );
};

export default FilterContainerPaymentMethod;
